import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormControlLabel,
  FormGroup,
  Checkbox,
  Chip,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Snackbar,
  Autocomplete,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  PersonAdd as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  SwapHoriz as TransferIcon,
  NavigateNext as NavigateNextIcon,
  Business as OrgIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';

function ProjectMemberManagement() {
  const { id } = useParams();
  // Opened in a standalone tab (e.g. from the membership tree): drop the breadcrumbs and condense
  // so it reads as a focused pop-out rather than a full in-app page.
  const [searchParams] = useSearchParams();
  const standalone = searchParams.get('standalone') === '1';

  const { user } = useAuth();

  const [project, setProject] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);

  // Dialog states
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [editMemberOpen, setEditMemberOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Organizations for transfer
  const [organizations, setOrganizations] = useState([]);
  const [transferType, setTransferType] = useState('user');

  // Form data
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState({
    can_view: true,
    can_use: false,
    can_edit: false,
    can_manage_members: false,
    can_delete: false,
    can_manage_storage: false
  });
  const [editingMember, setEditingMember] = useState(null);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [transferTarget, setTransferTarget] = useState(null);
  const [transferReason, setTransferReason] = useState('');

  // Organization members expansion
  const [orgMembersExpanded, setOrgMembersExpanded] = useState({});
  const [orgMembersData, setOrgMembersData] = useState({});
  const [orgMembersLoading, setOrgMembersLoading] = useState({});

  // Organizations granted access to this project (org-level shares)
  const [orgMembers, setOrgMembers] = useState([]);
  const [addOrgOpen, setAddOrgOpen] = useState(false);
  const [editOrgOpen, setEditOrgOpen] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [selectedOrgPermissions, setSelectedOrgPermissions] = useState({
    can_view: true, can_use: false, can_edit: false,
    can_manage_members: false, can_delete: false, can_manage_storage: false
  });

  // Notifications
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [projectResponse, membersResponse] = await Promise.all([
        apiService.getProject(id),
        apiService.getProjectMembers(id),
      ]);

      setProject(projectResponse.data);
      setMembers(membersResponse.data?.members || []);
      setOrgMembers(membersResponse.data?.organization_members || []);

      // Load users for Add Member autocomplete — may fail for non-admin users
      try {
        const usersResponse = await apiService.getUsers();
        setUsers(usersResponse.data?.users || usersResponse.data || []);
      } catch {
        setUsers([]);
      }

      // Load organizations for transfer: admins see all, others see their own
      try {
        const orgResponse = user?.role === 'server_admin'
          ? await apiService.getOrganizations()
          : await apiService.getUserOrganizations();
        setOrganizations(orgResponse.data?.organizations || orgResponse.data || []);
      } catch {
        setOrganizations([]);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      setSnackbar({ open: true, message: 'Failed to load data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Control Center panes refresh off a `projectChanged` window message, and
  // ControlCenter relays it to every other open floating iframe. This page is
  // usually opened as one of those iframes, so without the notification the
  // people:projects pane keeps showing the pre-change owner and member count
  // until it is manually reloaded (#1182).
  const notifyProjectChanged = () => {
    try {
      const msg = { type: 'projectChanged' };
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(msg, '*');
      } else {
        window.postMessage(msg, '*');
      }
    } catch (_) { /* no parent / cross-origin */ }
  };

  const handleAddMember = async () => {
    if (!selectedUser) return;

    try {
      const response = await apiService.addProjectMember(id, {
        user_id: selectedUser._id || selectedUser.id,
        permissions: selectedPermissions
      });

      if (response && response.success === false) {
        const errorMessage = response.error?.message || response.error || 'Failed to add member';
        setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        return;
      }

      setAddMemberOpen(false);
      setSelectedUser(null);
      setSelectedPermissions({ can_view: true, can_use: false, can_edit: false, can_manage_members: false, can_delete: false, can_manage_storage: false });
      setSnackbar({ open: true, message: 'Member added successfully', severity: 'success' });
      loadData();
      notifyProjectChanged();
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || error.response?.data?.error || 'Failed to add member';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const handleUpdatePermissions = async () => {
    if (!editingMember) return;

    try {
      const userId = editingMember.user_id?._id || editingMember.user_id;
      const response = await apiService.updateProjectMemberPermissions(id, userId, selectedPermissions);

      if (response && response.success === false) {
        const errorMessage = response.error?.message || response.error || 'Failed to update permissions';
        setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        return;
      }

      setEditMemberOpen(false);
      setEditingMember(null);
      setSnackbar({ open: true, message: 'Permissions updated successfully', severity: 'success' });
      loadData();
      notifyProjectChanged();
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || error.response?.data?.error || 'Failed to update permissions';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const handlePermissionChange = (permission) => (event) => {
    setSelectedPermissions(prev => ({
      ...prev,
      [permission]: event.target.checked
    }));
  };

  const handleRemoveMember = async () => {
    if (!memberToDelete) return;

    try {
      const userId = memberToDelete.user_id?._id || memberToDelete.user_id;
      const response = await apiService.removeProjectMember(id, userId);

      if (response && response.success === false) {
        const errorMessage = response.error?.message || response.error || 'Failed to remove member';
        setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        return;
      }

      setDeleteConfirmOpen(false);
      setMemberToDelete(null);
      setSnackbar({ open: true, message: 'Member removed successfully', severity: 'success' });
      loadData();
      notifyProjectChanged();
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || error.response?.data?.error || 'Failed to remove member';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const handleOrgPermissionChange = (permission) => (event) => {
    setSelectedOrgPermissions(prev => ({ ...prev, [permission]: event.target.checked }));
  };

  const handleAddOrganization = async () => {
    if (!selectedOrganization) return;
    try {
      const orgId = selectedOrganization._id || selectedOrganization.id;
      const response = await apiService.addProjectOrganization(id, orgId, selectedOrgPermissions);
      if (response && response.success === false) {
        setSnackbar({ open: true, message: response.error?.message || response.error || 'Failed to add organization', severity: 'error' });
        return;
      }
      setAddOrgOpen(false);
      setSelectedOrganization(null);
      setSelectedOrgPermissions({ can_view: true, can_use: false, can_edit: false, can_manage_members: false, can_delete: false, can_manage_storage: false });
      setSnackbar({ open: true, message: 'Organization added successfully', severity: 'success' });
      loadData();
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || error.response?.data?.error || 'Failed to add organization';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const handleUpdateOrgPermissions = async () => {
    if (!editingOrg) return;
    const orgId = editingOrg.organization_id?._id || editingOrg.organization_id;
    try {
      const response = await apiService.updateProjectOrganizationPermissions(id, orgId, selectedOrgPermissions);
      if (response && response.success === false) {
        setSnackbar({ open: true, message: response.error?.message || response.error || 'Failed to update permissions', severity: 'error' });
        return;
      }
      setEditOrgOpen(false);
      setEditingOrg(null);
      setSnackbar({ open: true, message: 'Organization permissions updated', severity: 'success' });
      loadData();
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || error.response?.data?.error || 'Failed to update permissions';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const handleRemoveOrganization = async (orgMember) => {
    const orgId = orgMember.organization_id?._id || orgMember.organization_id;
    try {
      const response = await apiService.removeProjectOrganization(id, orgId);
      if (response && response.success === false) {
        setSnackbar({ open: true, message: response.error?.message || response.error || 'Failed to remove organization', severity: 'error' });
        return;
      }
      setSnackbar({ open: true, message: 'Organization removed', severity: 'success' });
      loadData();
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || error.response?.data?.error || 'Failed to remove organization';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const handleToggleOrgMembers = async (orgId) => {
    const isExpanded = orgMembersExpanded[orgId];
    setOrgMembersExpanded(prev => ({ ...prev, [orgId]: !isExpanded }));

    // Load members if not already loaded
    if (!isExpanded && !orgMembersData[orgId]) {
      setOrgMembersLoading(prev => ({ ...prev, [orgId]: true }));
      try {
        const response = await apiService.getOrganizationMembers(orgId);
        const membersList = response.data?.members || [];
        setOrgMembersData(prev => ({ ...prev, [orgId]: membersList }));
      } catch (err) {
        console.error('Failed to load organization members:', err);
        setOrgMembersData(prev => ({ ...prev, [orgId]: [] }));
      } finally {
        setOrgMembersLoading(prev => ({ ...prev, [orgId]: false }));
      }
    }
  };

  const handleTransfer = async () => {
    if (!transferTarget) return;

    try {
      const targetId = transferTarget._id || transferTarget.id;
      const response = await apiService.transferProjectOwnership(id, transferType, targetId, transferReason || null);

      if (response && response.success === false) {
        const errorMessage = response.error?.message || response.error || 'Failed to transfer ownership';
        setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        return;
      }

      setTransferOpen(false);
      setTransferTarget(null);
      setTransferType('user');
      setTransferReason('');
      setSnackbar({ open: true, message: 'Ownership transferred successfully', severity: 'success' });
      loadData();
      notifyProjectChanged();
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || error.response?.data?.error || 'Failed to transfer ownership';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  // Helper to get permission chips for display
  const getPermissionChips = (member) => {
    const perms = member.permissions || {};
    const chips = [];

    if (member.is_owner) {
      chips.push({ label: 'Owner', color: 'error' });
    }
    if (perms.can_delete) {
      chips.push({ label: 'Delete', color: 'error' });
    }
    if (perms.can_manage_members) {
      chips.push({ label: 'Manage Members', color: 'warning' });
    }
    if (perms.can_manage_storage) {
      chips.push({ label: 'Manage Storage', color: 'info' });
    }
    if (perms.can_edit) {
      chips.push({ label: 'Edit', color: 'primary' });
    }
    if (perms.can_use) {
      chips.push({ label: 'Use', color: 'success' });
    }
    if (perms.can_view) {
      chips.push({ label: 'View', color: 'default' });
    }

    return chips.length > 0 ? chips : [{ label: 'No permissions', color: 'default' }];
  };

  // Helper to extract permissions from a member for editing
  const getMemberPermissions = (member) => {
    if (member.permissions && typeof member.permissions === 'object') {
      return {
        can_view: member.permissions.can_view || false,
        can_use: member.permissions.can_use || false,
        can_edit: member.permissions.can_edit || false,
        can_manage_members: member.permissions.can_manage_members || false,
        can_delete: member.permissions.can_delete || false,
        can_manage_storage: member.permissions.can_manage_storage || false
      };
    }
    return { can_view: true, can_use: false, can_edit: false, can_manage_members: false, can_delete: false, can_manage_storage: false };
  };

  // Check if current user can manage members
  const canManageMembers = project?.user_permissions?.can_manage_members ||
    project?.owner_id?._id === user?.id ||
    project?.owner_id === user?.id ||
    user?.role === 'server_admin';

  // Check if current user is owner (can transfer)
  const isOwner = project?.owner_id?._id === user?.id ||
    project?.owner_id === user?.id ||
    user?.role === 'server_admin';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: standalone ? 2 : 3 }}>
      {/* Breadcrumbs — suppressed in the standalone pop-out. */}
      {!standalone && (
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Link to="/projects" style={{ textDecoration: 'none', color: 'inherit' }}>
            Projects
          </Link>
          <Typography color="text.primary">{project?.name || 'Project'}</Typography>
          <Typography color="text.primary">Members</Typography>
        </Breadcrumbs>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: standalone ? 2 : 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant={standalone ? 'h6' : 'h4'}>
            {project?.name} - Members
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canManageMembers && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddMemberOpen(true)}
            >
              Add Member
            </Button>
          )}
          {canManageMembers && (
            <Button
              variant="outlined"
              startIcon={<OrgIcon />}
              onClick={() => setAddOrgOpen(true)}
            >
              Add Organization
            </Button>
          )}
          {isOwner && (
            <Button
              variant="outlined"
              startIcon={<TransferIcon />}
              onClick={() => setTransferOpen(true)}
            >
              Transfer Ownership
            </Button>
          )}
        </Box>
      </Box>

      {/* Ownership Info */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Ownership</Typography>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="body2" color="text.secondary">Owner</Typography>
              <Typography variant="body1">
                {project?.owner_type === 'organization' ? (
                  <>
                    {project?.owner_id?.name || 'Organization'}
                    {project?.owner_id?.short_name && (
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        ({project.owner_id.short_name})
                      </Typography>
                    )}
                  </>
                ) : (
                  <>
                    {project?.owner_id?.first_name} {project?.owner_id?.last_name}
                    {project?.owner_id?.email && (
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        ({project.owner_id.email})
                      </Typography>
                    )}
                  </>
                )}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Project Code</Typography>
              <Typography variant="body1" fontFamily="monospace">
                {project?.code || 'N/A'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Status</Typography>
              <Chip
                label={project?.status || 'active'}
                size="small"
                color={project?.status === 'active' ? 'success' : 'default'}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Members Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Members ({members.length}{project?.owner_type === 'organization' ? ' + 1 org' : ''})</Typography>
          {members.length === 0 && project?.owner_type !== 'organization' ? (
            <Alert severity="info">No members yet. Add members to collaborate on this project.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Permissions</TableCell>
                    <TableCell>Added</TableCell>
                    {canManageMembers && <TableCell align="right">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {project?.owner_type === 'organization' && (
                    <>
                      <TableRow sx={{ backgroundColor: 'action.hover' }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <OrgIcon fontSize="small" color="info" />
                            {project.owner_id?.name || 'Organization'}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">Organization</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label="Owner" color="error" size="small" />
                        </TableCell>
                        <TableCell>-</TableCell>
                        <TableCell align="right">
                          <Tooltip title={orgMembersExpanded[project.owner_id?._id] ? 'Hide members' : 'Show members'}>
                            <IconButton size="small" onClick={() => handleToggleOrgMembers(project.owner_id?._id)}>
                              {orgMembersExpanded[project.owner_id?._id] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      {orgMembersExpanded[project.owner_id?._id] && (
                        orgMembersLoading[project.owner_id?._id] ? (
                          <TableRow>
                            <TableCell colSpan={canManageMembers ? 5 : 4} sx={{ pl: 6 }}>
                              <CircularProgress size={16} sx={{ mr: 1 }} />
                              <Typography component="span" variant="body2" color="text.secondary">Loading members...</Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          (orgMembersData[project.owner_id?._id] || []).map((orgMember) => {
                            const memberId = orgMember.user_id?._id || orgMember.user_id;
                            const memberName = orgMember.user_id?.display_name ||
                              (orgMember.user_id?.first_name ? `${orgMember.user_id.first_name} ${orgMember.user_id.last_name}` : null) ||
                              orgMember.user_id?.email || 'Unknown';
                            return (
                              <TableRow key={memberId} sx={{ backgroundColor: 'action.selected', opacity: 0.85 }}>
                                <TableCell sx={{ pl: 6 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PersonIcon fontSize="small" color="disabled" />
                                    {memberName}
                                  </Box>
                                </TableCell>
                                <TableCell>{orgMember.user_id?.email || '-'}</TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                    {orgMember.is_owner ? (
                                      <>
                                        <Chip label="View" size="small" variant="outlined" />
                                        <Chip label="Use" size="small" variant="outlined" />
                                        <Chip label="Edit" size="small" variant="outlined" />
                                        <Chip label="Members" size="small" variant="outlined" />
                                        <Chip label="Delete" size="small" variant="outlined" />
                                        <Chip label="Storage" size="small" variant="outlined" />
                                        <Chip label="Indirect" size="small" variant="outlined" color="info" />
                                        <Chip label="Project Owner" size="small" color="primary" />
                                      </>
                                    ) : (
                                      <>
                                        <Chip label="View" size="small" variant="outlined" />
                                        <Chip label="Use" size="small" variant="outlined" />
                                        <Chip label="Indirect" size="small" variant="outlined" color="info" />
                                      </>
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell>-</TableCell>
                                {canManageMembers && <TableCell />}
                              </TableRow>
                            );
                          })
                        )
                      )}
                    </>
                  )}
                  {members.map((member) => {
                    const memberUser = member.user_id;
                    const permChips = getPermissionChips(member);
                    return (
                      <TableRow key={member._id}>
                        <TableCell>
                          {memberUser?.first_name} {memberUser?.last_name}
                        </TableCell>
                        <TableCell>{memberUser?.email}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {permChips.map((chip, idx) => (
                              <Chip
                                key={idx}
                                label={chip.label}
                                color={chip.color}
                                size="small"
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {member.added_at ? new Date(member.added_at).toLocaleDateString('en-US') : 'N/A'}
                        </TableCell>
                        {canManageMembers && (
                          <TableCell align="right">
                            <Tooltip title={member.is_owner ? "Cannot edit owner permissions" : "Edit permissions"}>
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={member.is_owner}
                                  onClick={() => {
                                    setEditingMember(member);
                                    setSelectedPermissions(getMemberPermissions(member));
                                    setEditMemberOpen(true);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={member.is_owner ? "Cannot remove owner" : "Remove member"}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={member.is_owner}
                                  onClick={() => {
                                    setMemberToDelete(member);
                                    setDeleteConfirmOpen(true);
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Organizations with access (org-level shares) */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Organizations with access ({orgMembers.length})</Typography>
          {orgMembers.length === 0 ? (
            <Alert severity="info">No organizations have been granted access. Use &quot;Add Organization&quot; to share this project with all of an organization&apos;s members.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Organization</TableCell>
                    <TableCell>Permissions</TableCell>
                    <TableCell>Added</TableCell>
                    {canManageMembers && <TableCell align="right">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orgMembers.map((om, idx) => {
                    const org = om.organization_id;
                    const orgId = org?._id || org;
                    const orgName = org?.name || org?.short_name || 'Organization';
                    const p = om.permissions || {};
                    const chips = [
                      p.can_view && 'View', p.can_use && 'Use', p.can_edit && 'Edit',
                      p.can_manage_members && 'Members', p.can_delete && 'Delete', p.can_manage_storage && 'Storage'
                    ].filter(Boolean);
                    return (
                      <TableRow key={orgId || idx}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <OrgIcon fontSize="small" color="info" />
                            {orgName}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {chips.length
                              ? chips.map((c) => <Chip key={c} label={c} size="small" variant="outlined" />)
                              : <Typography variant="body2" color="text.secondary">—</Typography>}
                          </Box>
                        </TableCell>
                        <TableCell>{om.added_at ? new Date(om.added_at).toLocaleDateString('en-US') : '-'}</TableCell>
                        {canManageMembers && (
                          <TableCell align="right">
                            <Tooltip title="Edit permissions">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingOrg(om);
                                  setSelectedOrgPermissions({
                                    can_view: !!p.can_view, can_use: !!p.can_use, can_edit: !!p.can_edit,
                                    can_manage_members: !!p.can_manage_members, can_delete: !!p.can_delete, can_manage_storage: !!p.can_manage_storage
                                  });
                                  setEditOrgOpen(true);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove organization access">
                              <IconButton size="small" color="error" onClick={() => handleRemoveOrganization(om)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add Member Dialog */}
      <Dialog open={addMemberOpen} onClose={() => setAddMemberOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Member</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              options={users.filter(u => !members.some(m => (m.user_id?._id || m.user_id) === (u._id || u.id)))}
              getOptionLabel={(option) => `${option.first_name} ${option.last_name} (${option.email})`}
              value={selectedUser}
              onChange={(e, newValue) => setSelectedUser(newValue)}
              renderInput={(params) => <TextField {...params} label="Select User" />}
            />
            <FormControl component="fieldset">
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Permissions</Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedPermissions.can_view}
                      onChange={handlePermissionChange('can_view')}
                    />
                  }
                  label="View - Read-only access to project details"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedPermissions.can_use}
                      onChange={handlePermissionChange('can_use')}
                    />
                  }
                  label="Use - Submit observations to this project"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedPermissions.can_edit}
                      onChange={handlePermissionChange('can_edit')}
                    />
                  }
                  label="Edit - Modify project settings"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedPermissions.can_manage_members}
                      onChange={handlePermissionChange('can_manage_members')}
                    />
                  }
                  label="Manage Members - Add/remove project collaborators"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedPermissions.can_delete}
                      onChange={handlePermissionChange('can_delete')}
                    />
                  }
                  label="Delete - Permanently delete the project"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedPermissions.can_manage_storage}
                      onChange={handlePermissionChange('can_manage_storage')}
                    />
                  }
                  label="Manage Storage - Configure external storage destinations"
                />
              </FormGroup>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMemberOpen(false)}>Cancel</Button>
          <Button onClick={handleAddMember} variant="contained" disabled={!selectedUser}>
            Add Member
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Organization Dialog */}
      <Dialog open={addOrgOpen} onClose={() => setAddOrgOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Organization</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              options={organizations.filter((o) => !orgMembers.some((om) =>
                (om.organization_id?._id || om.organization_id)?.toString() === (o._id || o.id)?.toString()))}
              getOptionLabel={(option) => option.name || option.short_name || ''}
              value={selectedOrganization}
              onChange={(e, newValue) => setSelectedOrganization(newValue)}
              renderInput={(params) => <TextField {...params} label="Select Organization" />}
            />
            <FormControl component="fieldset">
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Permissions granted to the organization&apos;s members</Typography>
              <FormGroup>
                {[
                  ['can_view', 'View - Read-only access to project details'],
                  ['can_use', 'Use - Submit observations to this project'],
                  ['can_edit', 'Edit - Modify project settings'],
                  ['can_manage_members', 'Manage Members - Add/remove project collaborators'],
                  ['can_delete', 'Delete - Permanently delete the project'],
                  ['can_manage_storage', 'Manage Storage - Configure external storage destinations'],
                ].map(([key, label]) => (
                  <FormControlLabel
                    key={key}
                    control={<Checkbox checked={selectedOrgPermissions[key]} onChange={handleOrgPermissionChange(key)} />}
                    label={label}
                  />
                ))}
              </FormGroup>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOrgOpen(false)}>Cancel</Button>
          <Button onClick={handleAddOrganization} variant="contained" disabled={!selectedOrganization}>
            Add Organization
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Organization Permissions Dialog */}
      <Dialog open={editOrgOpen} onClose={() => setEditOrgOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Organization Permissions</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Updating permissions for: {editingOrg?.organization_id?.name || editingOrg?.organization_id?.short_name || 'Organization'}
            </Typography>
            <FormControl component="fieldset">
              <FormGroup>
                {[
                  ['can_view', 'View - Read-only access to project details'],
                  ['can_use', 'Use - Submit observations to this project'],
                  ['can_edit', 'Edit - Modify project settings'],
                  ['can_manage_members', 'Manage Members - Add/remove project collaborators'],
                  ['can_delete', 'Delete - Permanently delete the project'],
                  ['can_manage_storage', 'Manage Storage - Configure external storage destinations'],
                ].map(([key, label]) => (
                  <FormControlLabel
                    key={key}
                    control={<Checkbox checked={selectedOrgPermissions[key]} onChange={handleOrgPermissionChange(key)} />}
                    label={label}
                  />
                ))}
              </FormGroup>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOrgOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateOrgPermissions} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Permissions Dialog */}
      <Dialog open={editMemberOpen} onClose={() => setEditMemberOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Member Permissions</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Updating permissions for: {editingMember?.user_id?.first_name} {editingMember?.user_id?.last_name}
            </Typography>
            <FormControl component="fieldset">
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedPermissions.can_view}
                      onChange={handlePermissionChange('can_view')}
                    />
                  }
                  label="View - Read-only access to project details"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedPermissions.can_use}
                      onChange={handlePermissionChange('can_use')}
                    />
                  }
                  label="Use - Submit observations to this project"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedPermissions.can_edit}
                      onChange={handlePermissionChange('can_edit')}
                    />
                  }
                  label="Edit - Modify project settings"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedPermissions.can_manage_members}
                      onChange={handlePermissionChange('can_manage_members')}
                    />
                  }
                  label="Manage Members - Add/remove project collaborators"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedPermissions.can_delete}
                      onChange={handlePermissionChange('can_delete')}
                    />
                  }
                  label="Delete - Permanently delete the project"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedPermissions.can_manage_storage}
                      onChange={handlePermissionChange('can_manage_storage')}
                    />
                  }
                  label="Manage Storage - Configure external storage destinations"
                />
              </FormGroup>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditMemberOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdatePermissions} variant="contained">
            Update Permissions
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Remove Member</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove {memberToDelete?.user_id?.first_name} {memberToDelete?.user_id?.last_name} from this project?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleRemoveMember} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Ownership Dialog */}
      <Dialog open={transferOpen} onClose={() => { setTransferOpen(false); setTransferType('user'); setTransferTarget(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Transfer Ownership</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {transferType === 'user'
              ? 'Transferring ownership will give full control of this project to the new owner. You will become a regular member with all permissions.'
              : 'Transferring ownership to an organization will give full control of this project to the Owner of the selected Organization. You will become a regular member with all permissions.'}
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <ToggleButtonGroup
              value={transferType}
              exclusive
              onChange={(e, newType) => {
                if (newType) {
                  setTransferType(newType);
                  setTransferTarget(null);
                }
              }}
              fullWidth
              size="small"
            >
              <ToggleButton value="user">
                <PersonIcon sx={{ mr: 1 }} /> User
              </ToggleButton>
              <ToggleButton value="organization">
                <OrgIcon sx={{ mr: 1 }} /> Organization
              </ToggleButton>
            </ToggleButtonGroup>

            {transferType === 'user' ? (
              <Autocomplete
                options={users}
                getOptionLabel={(option) => `${option.first_name} ${option.last_name} (${option.email})`}
                value={transferTarget}
                onChange={(e, newValue) => setTransferTarget(newValue)}
                renderInput={(params) => <TextField {...params} label="Select New Owner" />}
              />
            ) : (
              <Autocomplete
                options={organizations}
                getOptionLabel={(option) => option.name || option.display_name || ''}
                value={transferTarget}
                onChange={(e, newValue) => setTransferTarget(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Organization"
                    helperText={user?.role === 'server_admin'
                      ? 'As a Server Administrator, you can transfer to any organization'
                      : 'Showing organizations you are a member of'}
                  />
                )}
                noOptionsText="No organizations available"
              />
            )}

            <TextField
              label="Reason (optional)"
              value={transferReason}
              onChange={(e) => setTransferReason(e.target.value)}
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTransferOpen(false); setTransferType('user'); setTransferTarget(null); }}>Cancel</Button>
          <Button onClick={handleTransfer} variant="contained" color="warning" disabled={!transferTarget}>
            Transfer Ownership
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ProjectMemberManagement;
